// lib
import { useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'

import { toggleCheckbox } from '@aytm/app/stats/page/sidebar/components/lib/utils'
import { holdScrollAnchor } from '@aytm/app/stats/lib/page/scroll-anchor'
import {
  allCollapsed,
  collapseParams,
  collapsibleQuestions as collapsibleQuestionsOf
} from '@aytm/app/stats/page/sidebar/view-options/lib/params'

// components
import { Checkbox } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'

// styles
import style from '@aytm/app/stats/page/sidebar/view-options/styles/ViewOptionsPanel.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const levelsOptions = (PageGlobals.CONFIDENCE_LEVELS || []).map(level => {
  return { key: level[0], text: level[1] }
})

const percentDecimalsOptions = Array.from({ length: 4 }, (_, i) => {
  return { key: i, text: String(i) }
})

const DESIGN_SIZE_THRESHOLD = 100000

const calcDesignSize = batches =>
  (batches || []).filter(b => b && !b.na).reduce((acc, b) => acc * Math.max(1, (b.answerVariants || []).length), 1)

const defaultCjdView = batches =>
  0 < batches?.length && calcDesignSize(batches) < DESIGN_SIZE_THRESHOLD ? 'full' : 'lite'

const resolveCjdView = (batches, cjvParam) =>
  cjvParam == 'lite' || cjvParam == 'full' ? cjvParam : defaultCjdView(batches)

const Params = ({ survey, model, report, features, onParamsChange }) => {
  const qlogicVisibilityDisabled = !survey?.cl_exists
  const qaliasesVisibilityDisabled = !survey?.cl_exists || !survey?.cl_aliases_exists

  const [chartLegendMode, setChartLegendMode] = useState('long' == model.chartLegendMode || false)
  const [rType, setRType] = useState('responses' == model.rType || false)
  const [qLogicVisibility, setQLogicVisibility] = useState('hide' == model.qLogicVisibility || false)
  const [qAliasesVisibility, setQAliasesVisibility] = useState('hide' == model.qAliasesVisibility || false)
  const [zoomLevel, setZoomLevel] = useState('max' == model.zoomLevel || false)

  const [confidenceLevel, setConfidenceLevel] = useState(model.confidenceLevel || 0.95)
  const [significanceLevel, setSignificanceLevel] = useState(model.significanceLevel || 0.9)
  const [percentDecimals, setPercentDecimals] = useState(model.percentDecimals)

  const confidenceLevelRef = useRef(null)
  confidenceLevelRef.current = confidenceLevel
  const significanceLevelRef = useRef(null)
  significanceLevelRef.current = significanceLevel

  const collapsibleQuestions = useMemo(
    () => collapsibleQuestionsOf(survey, report.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [survey.questions, survey.scripts, report.current.schema, report.current.virtual, report.current.storytellings]
  )

  const qCollapsed = useMemo(
    () => allCollapsed(collapsibleQuestions, report.current.params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collapsibleQuestions, report.current.params]
  )

  const cjQuestions = useMemo(
    () =>
      survey.questions.filter(
        q => report.current.schema.includes(`q${q.position}`) && (q.type == 'cjd' || q.type == 'cjc')
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [survey.questions, report.current.schema]
  )

  const allCjLite = useMemo(
    () =>
      0 < cjQuestions.length &&
      cjQuestions.every(q => {
        const key = (0 < q.position && q.position) || q.id
        return (
          'lite' == resolveCjdView(q.batches, report.current.params[`cjv${key}`]) ||
          'summary-chart' == report.current.params[`cjt${key}`]
        )
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cjQuestions, report.current.params]
  )

  // A switch sends its own flip from the click, not from an effect comparing it with the model: the
  // sidebar writes the new value into the model it already handed us without rendering again, so an
  // effect sees the model move under the next click and the sync below and the send undo each other
  // in a loop -- the checkbox ticking itself on and off.
  const sendSwitchParams = params => {
    // showing or hiding the logic and the aliases rewrites every question title at a new height
    if (Object.hasOwn(params, 'question_logic') || Object.hasOwn(params, 'question_aliases')) holdScrollAnchor()
    onParamsChange(params, false)
  }

  // Collapse / expand every collapsible question in one params update. Ported from the
  // legacy page-view handler; collapsing scrolls the page back to the top.
  const onToggleQuestions = collapsed => {
    if (collapsed) window.scrollTo(0, 0)

    onParamsChange(collapseParams(collapsibleQuestions, collapsed), false)
  }

  // Draw every conjoint in the attribute view, or give each one its own view back. Which param
  // carries that depends on the view the question defaults to: one that defaults to the lite
  // design rides on cjv, the rest on cjt. Ported from the legacy sidebar handler.
  const onToggleAllCjLite = forceLite => {
    const params = report.current.params
    const updateSection = {}

    cjQuestions.forEach(q => {
      const key = (0 < q.position && q.position) || q.id

      if ('lite' == defaultCjdView(q.batches)) {
        if (forceLite) {
          if ('full' == params[`cjv${key}`]) updateSection[`cjv${key}`] = null
        } else {
          updateSection[`cjv${key}`] = 'full'
          if ('summary-chart' == params[`cjt${key}`]) updateSection[`cjt${key}`] = null
        }
      } else if (forceLite) {
        updateSection[`cjt${key}`] = 'summary-chart'
      } else if ('summary-chart' == params[`cjt${key}`]) {
        updateSection[`cjt${key}`] = null
      }
    })

    // every conjoint is redrawn at a new height, so the reader is held on the question they are reading
    holdScrollAnchor()
    onParamsChange(updateSection, false)
  }

  const toggleChartLegendMode = () => {
    setChartLegendMode(!chartLegendMode)
    sendSwitchParams({ legend: (!chartLegendMode && 'long') || 'short' })
  }

  const toggleRType = () => {
    setRType(!rType)
    sendSwitchParams({ rType: (!rType && 'responses') || 'cases' })
  }

  // Unchecking "Hide aytm Logic" also clears "Hide @aliases", both in one params-update.
  const toggleQLogicVisibility = () => {
    const params = { question_logic: (!qLogicVisibility && 'hide') || 'show' }

    setQLogicVisibility(!qLogicVisibility)
    if (qLogicVisibility && qAliasesVisibility) {
      setQAliasesVisibility(false)
      params.question_aliases = 'show'
    }
    sendSwitchParams(params)
  }

  const toggleQAliasesVisibility = () => {
    setQAliasesVisibility(!qAliasesVisibility)
    sendSwitchParams({ question_aliases: (!qAliasesVisibility && 'hide') || 'show' })
  }

  const toggleZoomLevel = () => {
    setZoomLevel(!zoomLevel)
    sendSwitchParams({ zoom_level: (!zoomLevel && 'max') || 'min' })
  }

  useEffect(() => {
    setChartLegendMode('long' == model.chartLegendMode || false)
  }, [model.chartLegendMode])
  useEffect(() => {
    setRType('responses' == model.rType || false)
  }, [model.rType])
  useEffect(() => {
    setQLogicVisibility('hide' == model.qLogicVisibility || false)
  }, [model.qLogicVisibility])
  useEffect(() => {
    setQAliasesVisibility('hide' == model.qAliasesVisibility || false)
  }, [model.qAliasesVisibility])
  useEffect(() => {
    setZoomLevel('max' == model.zoomLevel || false)
  }, [model.zoomLevel])

  useEffect(() => {
    setConfidenceLevel(model.confidenceLevel)
    setSignificanceLevel(model.significanceLevel)
  }, [model.confidenceLevel, model.significanceLevel])

  useEffect(() => {
    setPercentDecimals(0 <= model.percentDecimals ? model.percentDecimals : null)
  }, [model.percentDecimals])

  // A level redraws every significance-tested chart, which on a heavy report runs for a second with no
  // frame in between, so like the precision below it is handed to a later task: the pick paints and the
  // list closes first. The clamp against the other level is read when the redraw runs, not at the pick.
  useNonInitialEffect(() => {
    const deferred = setTimeout(() =>
      onParamsChange(
        {
          confidence_level: confidenceLevel,
          confidence_level_secondary: Math.min(significanceLevelRef.current, confidenceLevel)
        },
        false
      )
    )

    return () => clearTimeout(deferred)
  }, [confidenceLevel, onParamsChange])
  useNonInitialEffect(() => {
    const deferred = setTimeout(() =>
      onParamsChange(
        {
          confidence_level: Math.max(confidenceLevelRef.current, significanceLevel),
          confidence_level_secondary: significanceLevel
        },
        false
      )
    )

    return () => clearTimeout(deferred)
  }, [significanceLevel, onParamsChange])

  // The report redraws on this, and on a heavy one that takes seconds with no frame in between --
  // so the list the reader just picked from stays painted over the panel until it finishes. Handing
  // the redraw to a later task lets the pick's own frame paint first, and the list closes on it.
  useNonInitialEffect(() => {
    const deferred = setTimeout(() => onParamsChange({ percent_decimals: percentDecimals }, false))

    return () => clearTimeout(deferred)
  }, [percentDecimals, onParamsChange])

  return (
    <>
      <div className={style.section}>
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-12`}>
            <Checkbox
              data-testid="stat-view-options-legend-mode"
              className="p-checkbox-sm"
              aria-labelledby="stat-view-options-legend-mode-label"
              checked={chartLegendMode}
              disabled={features.view_options.disabled}
              onChange={features.view_options.disabled ? null : toggleChartLegendMode}
              onKeyDown={features.view_options.disabled ? null : event => toggleCheckbox(event, toggleChartLegendMode)}
            />
            <span
              id="stat-view-options-legend-mode-label"
              data-testid="stat-view-options-legend-mode-label"
              className={classNames(style['row-label'], style['row-label-sm'], {
                'p-disabled': features.view_options.disabled
              })}
              onClick={features.view_options.disabled ? null : toggleChartLegendMode}
            >
              Use Answers as labels instead of {'pie' == model.chartMode ? '%' : '"A#"'}
            </span>
          </div>
        </div>
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-12`}>
            <Checkbox
              data-testid="stat-view-options-rtype"
              className="p-checkbox-sm"
              aria-labelledby="stat-view-options-rtype-label"
              checked={rType}
              disabled={features.view_options.disabled}
              onChange={features.view_options.disabled ? null : toggleRType}
              onKeyDown={features.view_options.disabled ? null : event => toggleCheckbox(event, toggleRType)}
            />
            <span
              id="stat-view-options-rtype-label"
              data-testid="stat-view-options-rtype-label"
              className={classNames(style['row-label'], style['row-label-sm'], {
                'p-disabled': features.view_options.disabled
              })}
              onClick={features.view_options.disabled ? null : toggleRType}
            >
              Count responses in multiple choice Qs
            </span>
          </div>
        </div>
        {qlogicVisibilityDisabled ? null : (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-view-options-qlogic-visibility"
                className="p-checkbox-sm"
                aria-labelledby="stat-view-options-qlogic-visibility-label"
                checked={qLogicVisibility}
                disabled={features.view_options.disabled}
                onChange={features.view_options.disabled ? null : toggleQLogicVisibility}
                onKeyDown={
                  features.view_options.disabled ? null : event => toggleCheckbox(event, toggleQLogicVisibility)
                }
              />
              <span
                id="stat-view-options-qlogic-visibility-label"
                data-testid="stat-view-options-qlogic-visibility-label"
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': features.view_options.disabled
                })}
                onClick={features.view_options.disabled ? null : toggleQLogicVisibility}
              >
                Hide aytm Logic
              </span>
            </div>
          </div>
        )}
        {qaliasesVisibilityDisabled ? null : (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-view-options-qaliases-visibility"
                className="p-checkbox-sm"
                aria-labelledby="stat-view-options-qaliases-visibility-label"
                checked={qAliasesVisibility}
                disabled={features.view_options.disabled}
                onChange={features.view_options.disabled ? null : toggleQAliasesVisibility}
                onKeyDown={
                  features.view_options.disabled ? null : event => toggleCheckbox(event, toggleQAliasesVisibility)
                }
              />
              <span
                id="stat-view-options-qaliases-visibility-label"
                data-testid="stat-view-options-qaliases-visibility-label"
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': features.view_options.disabled
                })}
                onClick={features.view_options.disabled ? null : toggleQAliasesVisibility}
              >
                Hide @aliases
              </span>
            </div>
          </div>
        )}
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-12`}>
            <Checkbox
              data-testid="stat-view-options-zoom-charts"
              className="p-checkbox-sm"
              aria-labelledby="stat-view-options-zoom-charts-label"
              checked={zoomLevel}
              disabled={'charts' != model.mode}
              onChange={'charts' != model.mode ? null : toggleZoomLevel}
              onKeyDown={'charts' != model.mode ? null : event => toggleCheckbox(event, toggleZoomLevel)}
            />
            <span
              id="stat-view-options-zoom-charts-label"
              data-testid="stat-view-options-zoom-charts-label"
              className={classNames(style['row-label'], style['row-label-sm'], {
                'p-disabled': 'charts' != model.mode
              })}
              onClick={'charts' != model.mode ? null : toggleZoomLevel}
            >
              Zoom out all charts to full scale
            </span>
          </div>
        </div>
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-12`}>
            <Checkbox
              data-testid="stat-view-options-qcollapse"
              data-view-toggle-collapse-all
              className="p-checkbox-sm"
              aria-labelledby="stat-view-options-qcollapse-label"
              checked={qCollapsed}
              disabled={features.toggling.disabled}
              onChange={features.toggling.disabled ? null : () => onToggleQuestions(!qCollapsed)}
              onKeyDown={
                features.toggling.disabled ? null : event => toggleCheckbox(event, () => onToggleQuestions(!qCollapsed))
              }
            />
            <span
              id="stat-view-options-qcollapse-label"
              data-testid="stat-view-options-qcollapse-label"
              className={classNames(style['row-label'], style['row-label-sm'], {
                'p-disabled': features.toggling.disabled
              })}
              onClick={features.toggling.disabled ? null : () => onToggleQuestions(!qCollapsed)}
            >
              Collapse all questions
            </span>
          </div>
        </div>
        {0 < cjQuestions.length ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-view-options-cj-global-lite"
                className="p-checkbox-sm"
                aria-labelledby="stat-view-options-cj-global-lite-label"
                checked={Boolean(allCjLite)}
                disabled={features.view_options.disabled}
                onChange={features.view_options.disabled ? null : () => onToggleAllCjLite(!allCjLite)}
                onKeyDown={
                  features.view_options.disabled
                    ? null
                    : event => toggleCheckbox(event, () => onToggleAllCjLite(!allCjLite))
                }
              />
              <span
                id="stat-view-options-cj-global-lite-label"
                data-testid="stat-view-options-cj-global-lite-label"
                className={classNames(style['row-label'], style['row-label-sm'], {
                  'p-disabled': features.view_options.disabled
                })}
                onClick={features.view_options.disabled ? null : () => onToggleAllCjLite(!allCjLite)}
              >
                Show all Conjoints in Attribute view
              </span>
            </div>
          </div>
        ) : null}
      </div>

      <div className={style.section}>
        <div className={style['section-title']}>Confidence Levels for Sigtesting</div>

        <div className={classNames(style.row, style['row-double'])}>
          <div className="p-fluid p-col">
            <span id="stat-view-options-confidence-level-label" className={style['row-note']}>
              Primary
            </span>
            <Dropdown
              id="stat-view-options-confidence-level"
              className="p-dropdown-rounded"
              aria-labelledby="stat-view-options-confidence-level-label"
              ariaLabel="Primary confidence level"
              value={confidenceLevel}
              options={levelsOptions}
              optionValue="key"
              optionLabel="text"
              fixedDropdownDir="bottom"
              filter={false}
              disabled={features.view_options.disabled}
              onChange={features.view_options.disabled ? null : event => setConfidenceLevel(event.value)}
            />
          </div>
          <div className="p-fluid p-col">
            <span id="stat-view-options-significance-level-label" className={style['row-note']}>
              Secondary
            </span>
            <Dropdown
              id="stat-view-options-significance-level"
              className="p-dropdown-rounded"
              aria-labelledby="stat-view-options-significance-level-label"
              ariaLabel="Secondary confidence level"
              value={significanceLevel}
              options={levelsOptions}
              optionValue="key"
              optionLabel="text"
              fixedDropdownDir="bottom"
              filter={false}
              disabled={features.view_options.disabled}
              onChange={features.view_options.disabled ? null : event => setSignificanceLevel(event.value)}
            />
          </div>
        </div>
      </div>

      <div className={style.section}>
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col`}>
            <Checkbox
              data-testid="stat-view-options-dataRounding"
              className="p-checkbox-sm"
              aria-labelledby="stat-view-options-dataRounding-label"
              value="dataRounding"
              checked={null !== percentDecimals}
              disabled={features.view_options.disabled}
              onChange={
                features.view_options.disabled ? null : () => setPercentDecimals(null === percentDecimals ? 1 : null)
              }
              onKeyDown={
                features.view_options.disabled
                  ? null
                  : event => toggleCheckbox(event, () => setPercentDecimals(null === percentDecimals ? 1 : null))
              }
            />
            <span
              id="stat-view-options-dataRounding-label"
              data-testid="stat-view-options-dataRounding-label"
              className={classNames(style['row-label'], style['row-label-sm'], {
                'p-disabled': features.view_options.disabled
              })}
              onClick={
                features.view_options.disabled ? null : () => setPercentDecimals(null === percentDecimals ? 1 : null)
              }
            >
              Percentage decimal place
            </span>
          </div>
          {null === percentDecimals ? null : (
            <div className={`${style.decimals} p-fluid p-col`}>
              <Dropdown
                data-testid="stat-view-options-dataRounding-selector"
                className="p-dropdown-sm p-dropdown-rounded"
                aria-labelledby="stat-view-options-dataRounding-label"
                ariaLabel="Percentage decimal place"
                value={percentDecimals}
                options={percentDecimalsOptions}
                optionValue="key"
                optionLabel="text"
                fixedDropdownDir="bottom"
                filter={false}
                disabled={features.view_options.disabled}
                onChange={features.view_options.disabled ? null : event => setPercentDecimals(event.value)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

Params.propTypes = {
  survey: PropTypes.object,
  model: PropTypes.object,
  report: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  onParamsChange: PropTypes.func
}

Params.displayName = 'Params'

export default Params
